import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Search, Package, Plus } from 'lucide-react';
import { Modal } from '../../components/common';
import { catalogApi } from '../../services/api';
import './CatalogItemPicker.css';

export default function CatalogItemPicker({ isOpen, onClose, onSelect, excludeIds = [] }) {
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');

    const { data: items, isLoading } = useQuery({
        queryKey: ['catalogItems', search, category],
        queryFn: () => catalogApi.listItems({ search, categoryId: category, limit: 50 }),
        select: (res) => res.data?.data || [],
        enabled: isOpen
    });

    const { data: categories } = useQuery({
        queryKey: ['categories'],
        queryFn: () => catalogApi.listCategories({}),
        select: (res) => res.data?.data || [],
        enabled: isOpen
    });

    const filteredItems = items?.filter(item => !excludeIds.includes(item.id)) || [];

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Select Catalog Item"
            size="lg"
        >
            <div className="catalog-picker">
                <div className="picker-filters">
                    <div className="search-box">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search items..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        <option value="">All Categories</option>
                        {categories?.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>

                <div className="picker-items">
                    {isLoading ? (
                        <div className="picker-loading">Loading items...</div>
                    ) : filteredItems.length === 0 ? (
                        <div className="picker-empty">
                            <Package size={36} />
                            <p>No items found</p>
                        </div>
                    ) : (
                        filteredItems.map(item => (
                            <div key={item.id} className="picker-item">
                                <div className="picker-item-info">
                                    <span className="picker-item-code">{item.itemCode}</span>
                                    <span className="picker-item-name">{item.itemName}</span>
                                    <span className="picker-item-price">{formatCurrency(item.standardPrice)}</span>
                                </div>
                                <button
                                    className="picker-item-add"
                                    onClick={() => onSelect(item)}
                                >
                                    <Plus size={16} />
                                    Add
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </Modal>
    );
}

CatalogItemPicker.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    excludeIds: PropTypes.array
};
